/**
 * §16.1 wizard contract shared by the frame, the Template step and the feature agents' steps.
 *
 * Each step owns one or more top-level slices of `LotteryConfig` (§5.3). Validation issues are routed to the
 * step by the first path segment (`stepForPath`) and to the exact control by `fieldId(path)`.
 */
import {
  LotteryConfigSchema,
  SUPPORTED_CURRENCIES,
  exampleScheduledRaffleConfig,
  validateLotteryConfig,
  type ConfigIssue,
  type LotteryConfig,
  type LotteryMechanic,
  type MainMechanicBlock,
  type OutcomeRules,
} from '@windrop/contracts';
import type { z } from 'zod';

export const DraftConfigSchema = LotteryConfigSchema.omit({ configVersion: true });
export type DraftConfigInput = z.input<typeof DraftConfigSchema>;
export type DraftConfig = z.output<typeof DraftConfigSchema>;

export type WizardStepId =
  'template' | 'prizes' | 'sponsors' | 'engagement' | 'rules' | 'experience' | 'claims' | 'review';

export interface WizardStepDef {
  id: WizardStepId;
  label: string;
  /** §16.1 wording, shown as the step description. */
  description: string;
  /** Config slices this step edits (used by stubs and for routing issues). */
  slices: ReadonlyArray<keyof DraftConfig>;
}

export const WIZARD_STEPS: readonly WizardStepDef[] = [
  {
    id: 'template',
    label: 'Template',
    description: 'Pick the mechanic; set market policy, locale, currency and timezone.',
    slices: ['identity', 'outcome'],
  },
  {
    id: 'prizes',
    label: 'Prizes',
    description:
      'Products, option axes, variant matrix, images per colour, accent colour, values, stock, fulfillment owner; Mode B brand list and value cap.',
    slices: ['prizes'],
  },
  {
    id: 'sponsors',
    label: 'Sponsors & default consolation',
    description:
      'Logos, tiers, placements, reveal skins, prize linkage; sponsor-default prizes for main-prize losers with ranges, priority, reserved stock and copy.',
    slices: ['sponsors', 'sampling'],
  },
  {
    id: 'engagement',
    label: 'Engagement',
    description:
      'Social platforms and account links, follow/share/join/referral tasks, bonus values and caps, consent text.',
    slices: ['engagement'],
  },
  {
    id: 'rules',
    label: 'Rules',
    description:
      'Entry mode and price, eligibility, caps, schedule, outcome model details, odds disclosure, unclaimed-prize policy.',
    slices: ['entry', 'eligibility', 'schedule', 'outcome'],
  },
  {
    id: 'experience',
    label: 'Experience',
    description:
      'Theme tokens, energy level (full / calm), hero content, sound, motion preview, every result state.',
    slices: ['presentation'],
  },
  {
    id: 'claims',
    label: 'Claims',
    description: 'Verification adapter, required fields, retention class, claim window, service levels.',
    slices: ['claims'],
  },
  {
    id: 'review',
    label: 'Review & publish',
    description:
      'Automated validation, simulation report, second-person approval, seed commitment, share links and QR.',
    slices: [],
  },
];

export const stepIndex = (id: WizardStepId): number => WIZARD_STEPS.findIndex((s) => s.id === id);

/** `prizes.prizes[0].name` → `prizes.prizes.0.name` (react-hook-form / fieldId form). */
export const normalizePath = (path: string): string => path.replace(/\[(\d+)\]/g, '.$1').replace(/^\./, '');

/** Which step a config path belongs to; the mechanic choice itself lives in Template, other outcome rules in Rules. */
export const stepForPath = (path: string): WizardStepId => {
  const p = normalizePath(path);
  const [head, second] = p.split('.');
  switch (head) {
    case 'identity':
      return 'template';
    case 'outcome':
      return second === 'mechanic' || second === 'baseMechanic' || second === undefined
        ? 'template'
        : 'rules';
    case 'prizes':
      return 'prizes';
    case 'sponsors':
    case 'sampling':
      return 'sponsors';
    case 'engagement':
      return 'engagement';
    case 'entry':
    case 'eligibility':
    case 'schedule':
      return 'rules';
    case 'presentation':
      return 'experience';
    case 'claims':
      return 'claims';
    default:
      return 'review';
  }
};

export interface PreviewIssue extends Omit<ConfigIssue, 'code'> {
  code: string;
  step: WizardStepId;
}

/**
 * Client-side preview of §5.4 validation: zod shape issues first, then `validateLotteryConfig`. The server stays
 * authoritative (`LotteryAdminDto.validation`); this exists so each issue links to its field while editing.
 */
export const previewIssues = (values: unknown, configVersion = 1): PreviewIssue[] => {
  const parsed = DraftConfigSchema.safeParse(values);
  if (!parsed.success) {
    return parsed.error.issues.map((i) => {
      const path = normalizePath(i.path.map(String).join('.'));
      return {
        path,
        code: 'schema',
        message: i.message,
        severity: 'error' as const,
        step: stepForPath(path),
      };
    });
  }
  const result = validateLotteryConfig(
    { configVersion, ...parsed.data },
    { paymentProviderConfigured: true },
  );
  return result.issues.map((i) => {
    const path = normalizePath(i.path);
    return { ...i, path, step: stepForPath(path) };
  });
};

// ---------------------------------------------------------------------------------------------------------
// §5.1 mechanic catalogue — explanation and best-use copy for the Template step
// ---------------------------------------------------------------------------------------------------------

export interface MechanicInfo {
  mechanic: LotteryMechanic;
  name: string;
  outcomeModel: string;
  decisionTime: string;
  bestUse: string;
}

export const MECHANIC_CATALOG: readonly MechanicInfo[] = [
  {
    mechanic: 'SCHEDULED_RAFFLE',
    name: 'Scheduled raffle',
    outcomeModel: 'Deterministic shuffle of an immutable eligible-entry snapshot using committed randomness.',
    decisionTime: 'Scheduled draw',
    bestUse: 'Vehicles, cash jackpots, flagship campaigns.',
  },
  {
    mechanic: 'SPIN_WHEEL',
    name: 'Spin the wheel',
    outcomeModel: 'Server result from a fixed-pool instant ticket or scheduled result, rendered as a wheel.',
    decisionTime: 'At spin or draw',
    bestUse: 'Mixed prize pools, sponsor theatre.',
  },
  {
    mechanic: 'INSTANT_WIN',
    name: 'Fixed-pool instant win',
    outcomeModel: 'Precommitted shuffled vector of N winning/losing tickets, assigned atomically.',
    decisionTime: 'Confirmed entry',
    bestUse: 'Gadgets, airtime, high-volume promotions.',
  },
  {
    mechanic: 'SCRATCH_CARD',
    name: 'Scratch card',
    outcomeModel: 'Same fixed-pool ticket result with an accessible scratch presentation.',
    decisionTime: 'Confirmed entry',
    bestUse: 'Casual repeat engagement.',
  },
  {
    mechanic: 'NUMBER_PICK',
    name: 'Number pick',
    outcomeModel:
      'Immutable player selections or Quick Pick compared with a committed winning-number draw and published match tiers.',
    decisionTime: 'Scheduled draw',
    bestUse: 'Classic lotto format.',
  },
  {
    mechanic: 'FIRST_N',
    name: 'First-N / milestone',
    outcomeModel:
      'Deterministic qualification by atomic entry or unique-participant sequence and published milestones.',
    decisionTime: 'Confirmed entry',
    bestUse: 'Launch stunts, countdowns.',
  },
  {
    mechanic: 'FREE_GIVEAWAY',
    name: 'Free giveaway',
    outcomeModel: 'Zero-price template around any mechanic above, with optional engagement tasks.',
    decisionTime: 'Mechanic-dependent',
    bestUse: 'Audience growth, sponsor sampling.',
  },
];

/** A schema-valid block for a mechanic, pointing at `prizeId` (the first configured prize). */
export const defaultMechanicBlock = (
  mechanic: Exclude<LotteryMechanic, 'FREE_GIVEAWAY'>,
  prizeId: string,
  prizeValueMinor = '0',
): MainMechanicBlock => {
  switch (mechanic) {
    case 'SCHEDULED_RAFFLE':
      return {
        mechanic,
        scheduledRaffle: { tiers: [{ tier: 'GRAND', prizeId, winners: 1 }], allowMultipleTierWins: false },
      };
    case 'INSTANT_WIN':
    case 'SPIN_WHEEL':
    case 'SCRATCH_CARD':
      return {
        mechanic,
        fixedPool: {
          poolSize: 1000,
          outcomes: [{ prizeId, count: 10 }],
          exhaustionBehaviour: 'PAUSE_NEW_OUTCOMES',
        },
      };
    case 'NUMBER_PICK':
      return {
        mechanic,
        numberPick: {
          range: { min: 1, max: 49 },
          pickCount: 6,
          matchTiers: [{ matches: 6, prizeId, prizeUnits: 1 }],
          quickPick: 'ALLOWED',
          maxLiabilityMinor: prizeValueMinor,
          multipleWinnersPolicy: 'EACH_FIXED_UNIT',
        },
      };
    case 'FIRST_N':
      return {
        mechanic,
        firstN: { countingMode: 'UNIQUE_PARTICIPANT', milestones: [{ position: 100, prizeId }] },
      };
  }
};

/** Rebuild `outcome` for a new mechanic choice, keeping odds disclosure, redraw policy and algorithm. */
export const outcomeForMechanic = (
  current: OutcomeRules,
  mechanic: LotteryMechanic,
  prizeId: string,
  prizeValueMinor: string,
  /** Explicit base for FREE_GIVEAWAY; omitted = keep the current base (or default to a scheduled raffle). */
  baseMechanic?: Exclude<LotteryMechanic, 'FREE_GIVEAWAY'>,
): OutcomeRules => {
  const common = {
    oddsDisclosure: current.oddsDisclosure,
    redrawPolicy: current.redrawPolicy,
    algorithmVersion: current.algorithmVersion,
  };
  if (mechanic === 'FREE_GIVEAWAY') {
    const base = baseMechanic
      ? defaultMechanicBlock(baseMechanic, prizeId, prizeValueMinor)
      : current.mechanic === 'FREE_GIVEAWAY'
        ? current.baseMechanic
        : defaultMechanicBlock('SCHEDULED_RAFFLE', prizeId, prizeValueMinor);
    return { mechanic, baseMechanic: base, ...common };
  }
  return { ...defaultMechanicBlock(mechanic, prizeId, prizeValueMinor), ...common } as OutcomeRules;
};

export const SUPPORTED_CURRENCY_OPTIONS = SUPPORTED_CURRENCIES.map((c) => ({ value: c, label: c }));

export const COMMON_TIMEZONES = [
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Accra',
  'Africa/Johannesburg',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'Asia/Dubai',
  'UTC',
];

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);

/** A schema-valid starting draft (D-108 example) with a fresh slug and a blank, honest title. */
export const newDraftConfig = (now = new Date()): DraftConfig => {
  const base = exampleScheduledRaffleConfig();
  const stamp = now.toISOString().slice(0, 16).replace(/[-:T]/g, '');
  const opensAt = new Date(now.getTime() + 24 * 3600_000);
  const closesAt = new Date(now.getTime() + 8 * 24 * 3600_000);
  const drawAt = new Date(closesAt.getTime() + 3600_000);
  const { configVersion: _v, ...rest } = base;
  const content = Object.fromEntries(
    Object.entries(rest.presentation.content).map(([locale, c]) => [
      locale,
      { ...c, title: 'Untitled lottery', prizeStatement: 'Describe the prize' },
    ]),
  );
  return {
    ...rest,
    identity: { ...rest.identity, slug: slugify(`draft-${stamp}`) },
    schedule: {
      ...rest.schedule,
      opensAt: opensAt.toISOString(),
      closesAt: closesAt.toISOString(),
      drawAt: drawAt.toISOString(),
    },
    presentation: { ...rest.presentation, content },
  } as DraftConfig;
};

export const toDraftInput = (config: LotteryConfig): DraftConfigInput => {
  const { configVersion: _v, ...rest } = config;
  return rest as DraftConfigInput;
};
